/**
 * Registre des icônes de carte, par trace et par catégorie.
 *
 * MapLibre conserve dans son atlas de textures toute image ajoutée au style,
 * jusqu'à ce qu'on l'en retire explicitement. Changer la couleur d'une trace,
 * son intervalle kilométrique ou le mode sombre des marqueurs fabrique un jeu
 * de clés entièrement neuf : sans purge, l'ancien reste en mémoire jusqu'au
 * prochain changement de style. La revue Android du 16 juillet 2026 chiffre la
 * fuite à environ 8 Mo par couleur essayée sur une grosse trace.
 *
 * Portage de `MapManager.trackIconKeys` et `replaceIconKeys`. La subtilité qui
 * justifie un registre plutôt qu'un simple retrait : **les clés portent la
 * couleur, pas la trace**, si bien que deux traces de même couleur partagent
 * leurs icônes de départ et d'arrivée. Retirer les clés de l'une casserait les
 * marqueurs de l'autre — une clé n'est donc rendue à la purge que si plus
 * aucun créneau ne la référence.
 */

/** Catégories d'icônes d'une trace, reprises des créneaux d'Android. */
export const ICON_CATEGORIES = ['base', 'km', 'custom'] as const

export type IconCategory = (typeof ICON_CATEGORIES)[number]

/** Créneau du registre : `<trackId>:<catégorie>`, comme côté Android. */
export function iconSlot(trackId: string, category: IconCategory): string {
  return `${trackId}:${category}`
}

export class IconRegistry {
  #slots = new Map<string, ReadonlySet<string>>()

  /**
   * Remplace le jeu de clés d'un créneau et renvoie celles devenues orphelines.
   *
   * Le registre ne retire rien lui-même : il décide, l'appelant applique. C'est
   * ce qui rend la règle de partage vérifiable sans carte.
   *
   * Un jeu vide **efface** le créneau au lieu d'y laisser un ensemble vide.
   * C'est ce qui distingue « cette trace n'a plus de pastilles », qui doit
   * purger, de « cette trace n'a jamais été enregistrée », qui doit rester
   * muet — sans quoi la première mise à jour d'une trace purgerait des clés
   * qu'une autre vient de poser.
   */
  replace(slot: string, keys: ReadonlySet<string>): string[] {
    const previous = this.#slots.get(slot)

    if (keys.size === 0) this.#slots.delete(slot)
    else this.#slots.set(slot, keys)

    if (previous === undefined) return []

    const orphans: string[] = []
    for (const key of previous) {
      if (keys.has(key)) continue
      if (this.#referencedElsewhere(key)) continue
      orphans.push(key)
    }
    return orphans
  }

  /**
   * Oublie tout.
   *
   * À rappeler au rechargement du style : ses images sont parties avec lui, et
   * un registre qui les croit encore présentes empêcherait de les reposer.
   */
  clear(): void {
    this.#slots.clear()
  }

  /** Nombre de créneaux suivis — sert à éprouver qu'aucun ne survit à une trace. */
  get size(): number {
    return this.#slots.size
  }

  #referencedElsewhere(key: string): boolean {
    // Le créneau courant porte déjà son nouveau jeu : les clés qu'il conserve
    // ont été écartées juste avant, c'est donc bien une autre trace qu'on lit.
    for (const keys of this.#slots.values()) {
      if (keys.has(key)) return true
    }
    return false
  }
}
